import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const SCROLL_OFFSET = 16;

const getMenuBarOffset = () => {
  const menuBar = document.querySelector('.menu-bar');

  return menuBar ? menuBar.getBoundingClientRect().height : 0;
};

const getScrollBehavior = (): ScrollBehavior => {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return prefersReducedMotion ? 'auto' : 'smooth';
};

const scrollErrorIntoView = (element: HTMLElement, behavior: ScrollBehavior) => {
  const main = document.querySelector('.wrapper .body');
  const scrollRoot = main && main.scrollHeight > main.clientHeight ? main : null;
  const offset = getMenuBarOffset() + SCROLL_OFFSET;

  if (scrollRoot) {
    const rootRect = scrollRoot.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const top = scrollRoot.scrollTop + elementRect.top - rootRect.top - offset;

    scrollRoot.scrollTo({ top: Math.max(0, top), behavior });
    return;
  }

  const top = element.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(0, top), left: 0, behavior });
};

/**
 * Shared hook for error-alert visibility and scroll-into-view.
 * Resets visibility when error changes (so new errors show), scrolls to the alert
 * when it's visible, and respects prefers-reduced-motion.
 */
export function useScrollToErrorAlert<T>(error: T | null | undefined) {
  const errorAlertRef = useRef<HTMLDivElement>(null);
  const [isErrorVisible, setIsErrorVisible] = useState(true);
  const prevErrorRef = useRef<T | null | undefined>(error);

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      setIsErrorVisible(true);
      prevErrorRef.current = error;
    } else if (error === null || error === undefined) {
      prevErrorRef.current = null;
    }
  }, [error]);

  useLayoutEffect(() => {
    if (!error || !isErrorVisible || !errorAlertRef.current) return;
    scrollErrorIntoView(errorAlertRef.current, getScrollBehavior());
  }, [error, isErrorVisible]);

  return { errorAlertRef, isErrorVisible, setIsErrorVisible };
}
